import "./index.scss";
import React, { useState } from "react";
import debounce from "lodash.debounce";
import { routes } from "routes/apiRoutes";
import makeRequest from "services/makeRequest";
import { Typography, Button } from "components";
import ReportSelectors from "./ReportSelectors";
import { AiOutlineDownload } from "react-icons/ai";
import HighLowAgentTable from "./Tables/HighLowAgentTable";
import { searchAgent } from "redux/actions/transactionActions";
import HighLowTerminalTable from "./Tables/HighLowTerminalTable";
import HighLowAgentModal from "./FilterModals/HighLowAgentModal";
import TerminalStatusesTable from "./Tables/TerminalStatusesTable";
import HighLowTerminalModal from "./FilterModals/HighLowTerminalModal";
import ActiveInactiveAgentTable from "./Tables/ActiveInactiveAgentTable";
import StatusesTerminalModal from "./FilterModals/StatusesTerminalModal";
import ActiveInactiveTerminalTable from "./Tables/ActiveInactiveTerminalTable";
import ActiveInactiveAgentsModal from "./FilterModals/ActiveInactiveAgentsModal";
import VerifiedUnverifiedAgentsTable from "./Tables/VerifiedUnverifiedAgentsTable";
import ActiveInactiveTerminalsModal from "./FilterModals/ActiveInactiveTerminalsModal";
import VerifiedUnverifiedAgentsModal from "./FilterModals/VerifiedUnverifiedAgentsModal";
import {
  formatUrlQuery,
  downloadFile,
  formatCurrencyToNumber,
} from "utils/helper";

const MainReport = () => {
  const [currentModal, setCurrentModal] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [tableToDisplay, setTableToDisplay] = useState(null);
  const [selectedTransactionTypes, setSelectedTransactionTypes] = useState([]);

  const [loading, setLoading] = useState(false);

  const [agentActivityData, setAgentActivityData] = useState({});
  const [agentPerformanceData, setAgentPerformanceData] = useState({});
  const [terminalActivityData, setTerminalActivityData] = useState({});
  const [terminalPerformanceData, setTerminalPerformanceData] = useState({});
  const [terminalStatusesData, setTerminalStatusesData] = useState({});
  const [verifiedUnverifiedAgentData, setVerifiedUnverifiedAgentData] =
    useState({});

  const [activeInactiveAgentQuery, setActiveInactiveAgentQuery] = useState({
    agentId: "",
    threshold: 20,
    category: 0,
    size: 10,
    number: 1,
  });

  const [activeInactiveTerminalQuery, setActiveInactiveTerminalQuery] =
    useState({
      agentId: "",
      threshold: 7,
      category: 0,
      size: 10,
      number: 1,
    });

  const [highLowTerminalQuery, setHighLowTerminalQuery] = useState({
    agentId: "",
    measureBy: 0,
    category: 0,
    transThresh: "",
    threshold: 7,
    size: 10,
    number: 1,
  });

  const [highLowAgentQuery, setHighLowAgentQuery] = useState({
    agentId: "",
    measureBy: 0,
    category: 0,
    transThresh: "",
    threshold: 7,
    size: 10,
    number: 1,
  });

  const [terminalStatusesQuery, setTerminalStatusesQuery] = useState({
    status: "",
    size: 10,
    number: 1,
  });

  const [verifiedUnverifiedAgentQuery, setVerifiedUnverifiedAgentQuery] =
    useState({
      agentId: "",
      category: "",
      size: 10,
      number: 1,
    });

  const [terminalActivitySelectedAgent, setTerminalActivitySelectedAgent] =
    useState("");

  const [
    terminalPerformanceSelectedAgent,
    setTerminalPerformanceSelectedAgent,
  ] = useState("");

  const handleSelectReport = (value) => {
    setSelectedReport(value);
    if (tableToDisplay !== value) {
      setTableToDisplay(null);
      setSelectedTransactionTypes([]);
      setSelectedRegions([]);
    }
  };

  const getAgentActivityData = async () => {
    try {
      setLoading(true);
      const newQuery = {
        ...parentQuery,
        ...activeInactiveAgentQuery,
      };

      const { data } = await makeRequest(
        "get",
        routes.fetchAgentActivity(formatUrlQuery(newQuery))
      );
      setAgentActivityData(data.data);
      setLoading(false);
      setTableToDisplay(activeInActiveAgents);
      handleModalClose();
    } catch (error) {}
  };

  const downloadAgentActivity = async () => {
    try {
      const newQuery = {
        ...parentQuery,
        ...activeInactiveAgentQuery,
      };

      const { data } = await makeRequest(
        "get",
        routes.downloadAgentActivity(formatUrlQuery(newQuery))
      );
      await downloadFile(
        data,
        `Agent Activity Report - ${Date.now()}`,
        "text/csv"
      );
    } catch (error) {}
  };

  const getAgentPerformanceData = async () => {
    try {
      let newHighLowAgentQuery = { ...highLowAgentQuery };
      newHighLowAgentQuery.transThresh = formatCurrencyToNumber(
        newHighLowAgentQuery.transThresh
      );
      setLoading(true);
      const newQuery = {
        ...parentQuery,
        ...newHighLowAgentQuery,
      };

      const { data } = await makeRequest(
        "get",
        routes.fetchAgentPerformance(formatUrlQuery(newQuery))
      );
      setAgentPerformanceData(data.data);
      setLoading(false);
      setTableToDisplay(highLowPerformingAgents);
      handleModalClose();
    } catch (error) {}
  };

  const downloadAgentPerformance = async () => {
    try {
      let newHighLowAgentQuery = { ...highLowAgentQuery };
      newHighLowAgentQuery.transThresh = formatCurrencyToNumber(
        newHighLowAgentQuery.transThresh
      );
      const newQuery = {
        ...parentQuery,
        ...newHighLowAgentQuery,
      };

      const { data } = await makeRequest(
        "get",
        routes.downloadAgentActivity(formatUrlQuery(newQuery))
      );
      await downloadFile(
        data,
        `Agent Performance Report - ${Date.now()}`,
        "text/csv"
      );
    } catch (error) {}
  };

  const getActiveInactiveTerminalData = async () => {
    try {
      setLoading(true);
      const newQuery = {
        ...parentQuery,
        ...activeInactiveTerminalQuery,
      };

      const { data } = await makeRequest(
        "get",
        routes.fetchTerminalActivity(formatUrlQuery(newQuery))
      );
      setTerminalActivityData(data.data);
      setLoading(false);
      setTableToDisplay(activeInActiveTerminals);
      handleModalClose();
    } catch (error) {}
  };

  const downloadTerminalActivity = async () => {
    try {
      const newQuery = {
        ...parentQuery,
        ...activeInactiveTerminalQuery,
      };
      const { data } = await makeRequest(
        "get",
        routes.downloadTerminalActivity(formatUrlQuery(newQuery))
      );
      await downloadFile(
        data,
        `Terminal Activity Report - ${Date.now()}`,
        "text/csv"
      );
    } catch (error) {}
  };

  const getTerminalPerformanceData = async () => {
    try {
      let newHighLowTerminalQuery = { ...highLowTerminalQuery };
      newHighLowTerminalQuery.transThresh = formatCurrencyToNumber(
        newHighLowTerminalQuery.transThresh
      );
      setLoading(true);
      const newQuery = {
        ...parentQuery,
        ...newHighLowTerminalQuery,
      };

      const { data } = await makeRequest(
        "get",
        routes.fetchTerminalPerformance(formatUrlQuery(newQuery))
      );
      setTerminalPerformanceData(data.data);
      setLoading(false);
      setTableToDisplay(highLowPerformingTerminals);
      handleModalClose();
    } catch (error) {}
  };

  const downloadTerminalPerformance = async () => {
    try {
      let newHighLowTerminalQuery = { ...highLowTerminalQuery };
      newHighLowTerminalQuery.transThresh = formatCurrencyToNumber(
        newHighLowTerminalQuery.transThresh
      );
      const newQuery = {
        ...parentQuery,
        ...newHighLowTerminalQuery,
      };
      const { data } = await makeRequest(
        "get",
        routes.downloadTerminalPerformance(formatUrlQuery(newQuery))
      );
      await downloadFile(
        data,
        `Terminal Performance Report - ${Date.now()}`,
        "text/csv"
      );
    } catch (error) {}
  };

  const getTerminalStatusesData = async () => {
    try {
      setLoading(true);
      const newQuery = {
        ...parentQuery,
        ...terminalStatusesQuery,
      };

      const { data } = await makeRequest(
        "get",
        routes.fetchTerminals(formatUrlQuery(newQuery))
      );
      setTerminalStatusesData(data.data);
      setLoading(false);
      setTableToDisplay(terminalStatuses);
      handleModalClose();
    } catch (error) {}
  };

  const downloadTerminalStatuses = async () => {
    try {
      const newQuery = {
        ...parentQuery,
        ...terminalStatusesQuery,
      };

      const { data } = await makeRequest(
        "get",
        routes.downloadTerminalStatuses(formatUrlQuery(newQuery))
      );
      await downloadFile(
        data,
        `Terminal Status Report - ${Date.now()}`,
        "text/csv"
      );
    } catch (error) {}
  };

  const getVerifiedUnverifiedAgentData = async () => {
    try {
      setLoading(true);
      const newQuery = {
        region: selectedRegions.map((region) => region.value),
        ...verifiedUnverifiedAgentQuery,
      };

      const { data } = await makeRequest(
        "get",
        routes.agentVerification(formatUrlQuery(newQuery))
      );
      setVerifiedUnverifiedAgentData(data.data);
      setLoading(false);
      setTableToDisplay(verifiedUnverifiedAgents);
      handleModalClose();
    } catch (error) {}
  };

  const downloadVerifiedUnverifiedAgent = async () => {
    try {
      const newQuery = {
        region: selectedRegions.map((region) => region.value),
        ...verifiedUnverifiedAgentQuery,
      };

      const { data } = await makeRequest(
        "get",
        routes.downloadAgentVerification(formatUrlQuery(newQuery))
      );
      await downloadFile(
        data,
        `Agent verification Report - ${Date.now()}`,
        "text/csv"
      );
    } catch (error) {}
  };

  const handleSelectTransTypes = (val) => setSelectedTransactionTypes(val);

  const handleSelectRegions = (val) => setSelectedRegions(val);

  const handleCurrentModalSelect = (val) => setCurrentModal(val);

  const handleModalClose = () => setCurrentModal(false);

  const handleActiveInActiveAgentQueryChange = ({ name, value }) => {
    setActiveInactiveAgentQuery({
      ...activeInactiveAgentQuery,
      [name]: value,
    });
  };

  const handleActiveInActiveTerminalQueryChange = ({ name, value }) => {
    if (name !== "number") activeInactiveTerminalQuery.number = 1;
    setActiveInactiveTerminalQuery({
      ...activeInactiveTerminalQuery,
      [name]: value,
    });
  };

  const handleHighLowTerminalQueryChange = ({ name, value }) => {
    if (name !== "number") highLowTerminalQuery.number = 1;
    setHighLowTerminalQuery({
      ...highLowTerminalQuery,
      [name]: value,
    });
  };

  const handleHighLowAgentQueryChange = ({ name, value }) => {
    if (name !== "number") highLowAgentQuery.number = 1;
    setHighLowAgentQuery({
      ...highLowAgentQuery,
      [name]: value,
    });
  };

  const handleTerminalStatusQueryChange = ({ name, value }) => {
    if (name !== "number") terminalStatusesQuery.number = 1;
    setTerminalStatusesQuery({
      ...terminalStatusesQuery,
      [name]: value,
    });
  };

  const handleVerifiedUnverifiedAgentQueryChange = ({ name, value }) => {
    if (name !== "number") verifiedUnverifiedAgentQuery.number = 1;
    setVerifiedUnverifiedAgentQuery({
      ...verifiedUnverifiedAgentQuery,
      [name]: value,
    });
  };

  const handleHighLowAgentMeasureByChange = (value) => {
    setHighLowAgentQuery({
      ...highLowAgentQuery,
      measureBy: value,
      transThresh: "",
    });
  };

  const handleHighLowTerminalMeasureByChange = (value) => {
    setHighLowTerminalQuery({
      ...highLowTerminalQuery,
      measureBy: value,
      transThresh: "",
    });
  };

  const agentSearchStyles = {
    control: (base) => ({
      ...base,
      border: "1px solid #102E88",
      width: "20rem",
      borderRadius: "12px",
    }),
    placeholder: (base) => ({ ...base, fontSize: "12px" }),
  };

  const _loadSuggestions = (searchValue, callback) => {
    searchAgent(searchValue).then((resp) => callback(resp));
  };

  const loadAgentsSuggestion = debounce(_loadSuggestions, 1000);

  const reportOptions = {
    activeInActiveAgents: "activeInActiveAgents",
    activeInActiveTerminals: "activeInActiveTerminals",
    highLowPerformingTerminals: "highLowPerformingTerminals",
    highLowPerformingAgents: "highLowPerformingAgents",
    terminalStatuses: "terminalStatuses",
    verifiedUnverifiedAgents: "verifiedUnverifiedAgents",
  };

  const {
    activeInActiveAgents,
    activeInActiveTerminals,
    highLowPerformingAgents,
    highLowPerformingTerminals,
    terminalStatuses,
    verifiedUnverifiedAgents,
  } = reportOptions;

  const reportSelectionProps = {
    selectedReport,
    selectedTransactionTypes,
    selectedRegions,
    reportOptions,
    handleSelectReport,
    handleSelectRegions,
    handleSelectTransTypes,
    handleCurrentModalSelect,
  };

  const activeInactiveAgentModalProps = {
    showModal: currentModal === activeInActiveAgents,
    activeInactiveAgentQuery,
    handleModalClose,
    handleActiveInActiveAgentQueryChange,
  };

  const activeInactiveTerminalModalProps = {
    showModal: currentModal === activeInActiveTerminals,
    activeInactiveTerminalQuery,
    loadAgentsSuggestion,
    agentSearchStyles,
    terminalActivitySelectedAgent,
    handleModalClose,
    setTerminalActivitySelectedAgent,
    handleActiveInActiveTerminalQueryChange,
  };

  const highLowTerminalModalProps = {
    showModal: currentModal === highLowPerformingTerminals,
    highLowTerminalQuery,
    loadAgentsSuggestion,
    agentSearchStyles,
    terminalPerformanceSelectedAgent,
    handleModalClose,
    handleMeasureByChange: handleHighLowTerminalMeasureByChange,
    handleHighLowTerminalQueryChange,
    setTerminalPerformanceSelectedAgent,
  };

  const highLowAgentModalProps = {
    showModal: currentModal === highLowPerformingAgents,
    highLowAgentQuery,
    handleModalClose,
    handleMeasureByChange: handleHighLowAgentMeasureByChange,
    handleHighLowAgentQueryChange,
  };

  const terminalStatusesModalProps = {
    showModal: currentModal === terminalStatuses,
    terminalStatusesQuery,
    handleModalClose,
    handleTerminalStatusQueryChange,
  };

  const verifiedUnverifiedAgentModalProps = {
    showModal: currentModal === verifiedUnverifiedAgents,
    verifiedUnverifiedAgentQuery,
    loadAgentsSuggestion,
    agentSearchStyles,
    handleModalClose,
    handleVerifiedUnverifiedAgentQueryChange,
  };

  const activeInactiveAgentTableProps = {
    loading,
    agentActivityData,
    activeInactiveAgentQuery,
    getAgentActivityData,
    downloadAgentActivity,
    handleActiveInActiveAgentQueryChange,
  };

  const highLowAgentsTableProps = {
    loading,
    agentPerformanceData,
    loadAgentsSuggestion,
    agentSearchStyles,
    highLowAgentQuery,
    getAgentPerformanceData,
    downloadAgentPerformance,
    handleHighLowAgentQueryChange,
  };

  const activeInactiveTerminalTableProps = {
    loading,
    terminalActivityData,
    loadAgentsSuggestion,
    agentSearchStyles,
    activeInactiveTerminalQuery,
    terminalActivitySelectedAgent,
    setTerminalActivitySelectedAgent,
    getActiveInactiveTerminalData,
    downloadTerminalActivity,
    handleActiveInActiveTerminalQueryChange,
  };

  const highLowTerminalTableProps = {
    loading,
    terminalPerformanceData,
    loadAgentsSuggestion,
    agentSearchStyles,
    highLowTerminalQuery,
    terminalPerformanceSelectedAgent,
    setTerminalPerformanceSelectedAgent,
    getTerminalPerformanceData,
    downloadTerminalPerformance,
    handleHighLowTerminalQueryChange,
  };

  const terminalStatusesTableProps = {
    loading,
    terminalStatusesData,
    terminalStatusesQuery,
    getTerminalStatusesData,
    downloadTerminalStatuses,
    handleTerminalStatusQueryChange,
  };

  const verifiedUnverifiedAgentTableProps = {
    loading,
    verifiedUnverifiedAgentData,
    verifiedUnverifiedAgentQuery,
    getVerifiedUnverifiedAgentData,
    downloadVerifiedUnverifiedAgent,
    handleVerifiedUnverifiedAgentQueryChange,
  };

  const parentQuery = {
    region: selectedRegions.map((region) => region.value),
    type: selectedTransactionTypes.map((type) => type.value),
  };

  const getReportData = () => {
    switch (selectedReport) {
      case activeInActiveAgents:
        return getAgentActivityData();

      case highLowPerformingAgents:
        return getAgentPerformanceData();

      case activeInActiveTerminals:
        return getActiveInactiveTerminalData();

      case highLowPerformingTerminals:
        return getTerminalPerformanceData();

      case terminalStatuses:
        return getTerminalStatusesData();

      case verifiedUnverifiedAgents:
        return getVerifiedUnverifiedAgentData();

      default:
        return null;
    }
  };

  const getTableToRender = () => {
    switch (tableToDisplay) {
      case activeInActiveAgents:
        return <ActiveInactiveAgentTable {...activeInactiveAgentTableProps} />;

      case highLowPerformingAgents:
        return <HighLowAgentTable {...highLowAgentsTableProps} />;

      case activeInActiveTerminals:
        return (
          <ActiveInactiveTerminalTable {...activeInactiveTerminalTableProps} />
        );

      case highLowPerformingTerminals:
        return <HighLowTerminalTable {...highLowTerminalTableProps} />;

      case terminalStatuses:
        return <TerminalStatusesTable {...terminalStatusesTableProps} />;

      case verifiedUnverifiedAgents:
        return (
          <VerifiedUnverifiedAgentsTable
            {...verifiedUnverifiedAgentTableProps}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="reports-container">
      <div className="reports-header">
        <Typography variant="heading3" state="primary" component="h3">
          {" "}
          Terminal Report
        </Typography>
        <Typography variant="subtitle3" state="primary" className={"subtitle"}>
          Create New Report
        </Typography>
      </div>
      <div className="reports-body">
        <ReportSelectors {...reportSelectionProps} />

        <div className="d-flex justify-content-center w-100 report-generate-cta">
          <Button
            label="Generate Report"
            endIcon={<AiOutlineDownload />}
            loading={loading}
            onClick={getReportData}
            disabled={!selectedReport}
          />
        </div>

        <div style={{ marginTop: "5rem" }}>{getTableToRender()}</div>

        <ActiveInactiveAgentsModal {...activeInactiveAgentModalProps} />
        <ActiveInactiveTerminalsModal {...activeInactiveTerminalModalProps} />
        <HighLowTerminalModal {...highLowTerminalModalProps} />
        <HighLowAgentModal {...highLowAgentModalProps} />
        <StatusesTerminalModal {...terminalStatusesModalProps} />
        <VerifiedUnverifiedAgentsModal {...verifiedUnverifiedAgentModalProps} />
      </div>
    </div>
  );
};

export default MainReport;
